import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { normalizeString, normalizeForMatch } from "../utils/string";
import { getBoundsFromGeometry } from "../utils/getBoundsFromGeometry";

const normalizeFeature = (f) => ({
    ...f,
    properties: {
        ...f.properties,
        municipio_normalizado: normalizeString(f.properties.MUNICIPIO),
        nucleo_normalizado: normalizeForMatch(f.properties.NUCLEO || ""),
        nm_cerurb_normalizado: normalizeForMatch(f.properties.NM_CERURB || ""),
        nucleo_casalegal_paineis_normalizado: normalizeForMatch(f.properties.NUCLEO_CASALEGAL_PAINEIS || ""),
        territorio_normalizado: normalizeString(f.properties.TERRITORIO || "")
    }
});

// Busca apenas as layers base (municipios, nucleos, territorios) + planilhas
const fetchBaseData = async () => {
    const API_KEY = process.env.REACT_APP_API_KEY;
    const API_URL = process.env.REACT_APP_API_URL;
    const authHeaders = { headers: { Authorization: `Bearer ${API_KEY}` } };

    const [municipiosRes, nucleosRes, territoriosRes, planilhaRes, resumoRes, controleRes] = await Promise.all([
        axios.get(`${API_URL}/dados/dadosbd/municipios`, authHeaders),
        axios.get(`${API_URL}/dados/dadosbd/nucleos`, authHeaders),
        axios.get(`${API_URL}/dados/dadosbd/territorios`, authHeaders),
        axios.get(`${API_URL}/dados/planilha-cerurb`, authHeaders),
        axios.get(`${API_URL}/dados/planilha-resumo`, authHeaders),
        axios.get(`${API_URL}/dados/planilha-controle`, authHeaders)
    ]);

    const features = [
        ...(municipiosRes.data.features || []),
        ...(nucleosRes.data.features || []),
        ...(territoriosRes.data.features || []),
    ].map(normalizeFeature);

    const planilhaData = planilhaRes.data.data || [];
    const planilhaControleData = controleRes.data.data || [];
    const estatisticasPlanilha = resumoRes.data.estatisticas || {};
    const territorioStats = estatisticasPlanilha.territorios || {};

    return { features, planilhaData, territorioStats, estatisticasPlanilha, planilhaControleData };
};

// Busca lotes e quadras apenas quando necessário (lazy)
const fetchDetailData = async () => {
    const API_KEY = process.env.REACT_APP_API_KEY;
    const API_URL = process.env.REACT_APP_API_URL;
    const authHeaders = { headers: { Authorization: `Bearer ${API_KEY}` } };

    const [lotesRes, quadrasRes] = await Promise.all([
        axios.get(`${API_URL}/dados/dadosbd/lotes`, authHeaders),
        axios.get(`${API_URL}/dados/dadosbd/quadras`, authHeaders),
    ]);

    return [
        ...(lotesRes.data.features || []),
        ...(quadrasRes.data.features || []),
    ].map(normalizeFeature);
};


export function useNucleoFilters(selectedTerritorio, selectedMunicipio, selectedNucleo, nucleosExternos, activeView, selectedStatus = null) {
    // Estados para dados brutos, carregamento e erro (MELHORIA ADICIONADA)
    const [allData, setAllData] = useState({ 
        features: [], 
        planilhaData: [], 
        planilhaControleData: [],
        territorioStats: {}, 
        estatisticasPlanilha: {} 
        
        
    });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    // Lotes e quadras: carregados apenas quando um núcleo é selecionado
    const [detailFeatures, setDetailFeatures] = useState([]);
    const [detailLoaded, setDetailLoaded] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Carrega municipios, nucleos e territorios uma única vez
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const data = await fetchBaseData();
                setAllData(data);
                setError(null);
            } catch (err) {
                console.error("Erro ao carregar dados base:", err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    // Pré-carrega lotes e quadras em background logo após os dados base estarem prontos,
    // evitando a espera de rede no momento em que o usuário seleciona um núcleo.
    useEffect(() => {
        if (loading || detailLoaded) return;
        const loadDetails = async () => {
            setLoadingDetails(true);
            try {
                const details = await fetchDetailData();
                setDetailFeatures(details);
                setDetailLoaded(true);
            } catch (err) {
                console.error("Erro ao carregar lotes/quadras:", err);
            } finally {
                setLoadingDetails(false);
            }
        };
        loadDetails();
    }, [loading, detailLoaded]);

    const { features, planilhaData, territorioStats, estatisticasPlanilha, planilhaControleData } = allData;

    // ... (O RESTANTE DO SEU CÓDIGO PERMANECE IGUAL) ...

    const municipioToTerritorioMap = useMemo(() => {
        const map = {};
        if (!features || features.length === 0) return map;

        features.forEach(feature => {
            const municipioNorm = feature.properties.municipio_normalizado;
            const territorioNorm = feature.properties.territorio_normalizado;
            // Garante que temos ambos os dados e que ainda não adicionamos este município ao mapa
            if (municipioNorm && territorioNorm && !map[municipioNorm]) {
                map[municipioNorm] = territorioNorm;
            }
        });
        return map;
    }, [features]);

    const territoriosOptions = useMemo(() => {
        const uniqueTerritorios = new Set(features.map(f => f.properties.TERRITORIO).filter(Boolean));
        return ["Todos", ...Array.from(uniqueTerritorios).sort()];
    }, [features]);

    const municipioToTerritorio = useMemo(() => {
        const map = {};
        features.forEach(f => {
            if (f.properties.MUNICIPIO && f.properties.TERRITORIO) {
                map[f.properties.MUNICIPIO] = f.properties.TERRITORIO;
            }
        });
        return map;
    }, [features]);

    // Memoiza as listas de opções para os filtros (LÓGICA PRESERVADA)
    const municipios = useMemo(() => {
        const territorioNorm = normalizeString(selectedTerritorio);
        const isTerritorioTodos = selectedTerritorio === "Todos";

        const featuresInTerritorio = isTerritorioTodos
            ? features
            : features.filter(f => f.properties.territorio_normalizado === territorioNorm);

        const uniqueMunicipios = new Set(featuresInTerritorio.map(f => f.properties.MUNICIPIO).filter(Boolean));
        return ["Todos", ...Array.from(uniqueMunicipios).sort()];
    }, [features, selectedTerritorio]);

    // Lógica para `municipiosFiltrados` e `nucleos` permanece a mesma,
    // pois elas dependem de `municipios` e `selectedMunicipio` que já são reativos.
    const municipiosFiltrados = useMemo(() => {
        if (activeView !== 'painel2') return municipios;
        const cidadesNucleosNorm = new Set(nucleosExternos.map(n => normalizeString(n.cidadeNucleo || "")));
        if (cidadesNucleosNorm.size === 0) return municipios;
        return municipios.filter(m => m === "Todos" || cidadesNucleosNorm.has(normalizeString(m)));
    }, [municipios, nucleosExternos, activeView]);

    const municipiosComNucleos = useMemo(() => {
        if (activeView === 'painel2') {
            return new Set(nucleosExternos.map(n => n.cidadeNucleo).filter(Boolean));
        }
        const campoNucleo = activeView === 'painel1' ? 'NUCLEO_CASALEGAL_PAINEIS' : 'NUCLEO';
        const result = new Set();
        features.forEach(f => {
            if (f.properties.TIPO?.toLowerCase() === "nucleo" && f.properties[campoNucleo] && f.properties.MUNICIPIO) {
                result.add(f.properties.MUNICIPIO);
            }
        });
        return result;
    }, [features, nucleosExternos, activeView]);

    const nucleoToMunicipioMap = useMemo(() => {
        const map = {};
        const campoNucleo = activeView === 'painel2'
            ? 'NM_CERURB'
            : activeView === 'painel1'
                ? 'NUCLEO_CASALEGAL_PAINEIS'
                : 'NUCLEO';

        // índice normalizado: municipio_norm → { municipio original, territorio original }
        const munNormIndex = {};
        features.forEach(f => {
            if (f.properties.MUNICIPIO && f.properties.TERRITORIO) {
                munNormIndex[f.properties.municipio_normalizado] = {
                    municipio: f.properties.MUNICIPIO,
                    territorio: f.properties.TERRITORIO,
                };
            }
        });

        features.forEach(f => {
            if (f.properties.TIPO?.toLowerCase() === "nucleo" && f.properties[campoNucleo] && f.properties.MUNICIPIO) {
                const entry = munNormIndex[f.properties.municipio_normalizado] || {};
                map[f.properties[campoNucleo]] = {
                    municipio: f.properties.MUNICIPIO,
                    territorio: f.properties.TERRITORIO || entry.territorio || "",
                };
            }
        });
        if (activeView === 'painel2') {
            nucleosExternos.forEach(n => {
                if (n.nome && n.cidadeNucleo) {
                    const entry = munNormIndex[normalizeString(n.cidadeNucleo)] || {};
                    map[n.nome] = {
                        municipio: entry.municipio || n.cidadeNucleo,
                        territorio: entry.territorio || "",
                    };
                }
            });
        }
        return map;
    }, [features, nucleosExternos, activeView]);

    // Mapa nome-do-nucleo → id (para link de edição)
    const nucleoGidMap = useMemo(() => {
        const map = {};
        const campoNucleo = activeView === 'painel2'
            ? 'NM_CERURB'
            : activeView === 'painel1'
                ? 'NUCLEO_CASALEGAL_PAINEIS'
                : 'NUCLEO';
        features.forEach(f => {
            if (f.properties.TIPO?.toLowerCase() === "nucleo" && f.properties.id) {
                const nome = (f.properties[campoNucleo] || "").trim();
                if (nome) map[nome] = f.properties.id;
            }
        });
        return map;
    }, [features, activeView]);

    const nucleos = useMemo(() => {
        const municipioNorm = normalizeString(selectedMunicipio);

        const allNucleoFeatures = features.filter(f => f.properties.TIPO?.toLowerCase() === "nucleo");

        const nucleosNoMunicipio = (selectedMunicipio === "Todos")
            ? allNucleoFeatures
            : allNucleoFeatures.filter(f => f.properties.municipio_normalizado === municipioNorm);

        const campoNucleo = activeView === 'painel2'
            ? 'NM_CERURB'
            : activeView === 'painel1'
                ? 'NUCLEO_CASALEGAL_PAINEIS'
                : 'NUCLEO';

        const nomes = nucleosNoMunicipio.map(f => (f.properties[campoNucleo] || "").trim());

        return ["Todos", ...Array.from(new Set(nomes.filter(Boolean))).sort()];

    }, [features, selectedMunicipio, activeView]);

    const filteredData = useMemo(() => {
        const territorioNorm = normalizeString(selectedTerritorio);
        const municipioNorm = normalizeString(selectedMunicipio);
        const nucleoNorm = normalizeForMatch(selectedNucleo);

        const isTerritorioTodos = selectedTerritorio === "Todos";
        const isMunicipioTodos = selectedMunicipio === "Todos";
        const isNucleoTodos = selectedNucleo === "Todos";


        // Combina features base (municipios/nucleos/territorios) com detalhes carregados sob demanda (lotes/quadras)
        const allFeatures = [...features, ...detailFeatures];

        // Para painel1: lotes/quadras frequentemente só têm NUCLEO ou NM_CERURB preenchidos,
        // não NUCLEO_CASALEGAL_PAINEIS. Constrói mapas de correspondência a partir das features
        // do tipo NUCLEO para encontrar lotes/quadras pelo campo que estiver disponível.
        let painelToNucleoNorm = null;
        let painelToNmCerurbNorm = null;
        if (activeView === 'painel1' && !isNucleoTodos) {
            painelToNucleoNorm = {};
            painelToNmCerurbNorm = {};
            features.forEach(f => {
                if (f.properties.TIPO?.toLowerCase() === 'nucleo') {
                    const painel = f.properties.nucleo_casalegal_paineis_normalizado;
                    if (painel) {
                        if (f.properties.nucleo_normalizado)    painelToNucleoNorm[painel]    = f.properties.nucleo_normalizado;
                        if (f.properties.nm_cerurb_normalizado) painelToNmCerurbNorm[painel] = f.properties.nm_cerurb_normalizado;
                    }
                }
            });
        }

        const filteredFeatures = allFeatures.filter(f => {
            const itemMunicipioNorm = f.properties.municipio_normalizado;
            const itemTerritorioNorm = municipioToTerritorioMap[itemMunicipioNorm] || f.properties.territorio_normalizado;

            const territorioMatch = isTerritorioTodos || itemTerritorioNorm === territorioNorm;
            const municipioMatch = isMunicipioTodos || itemMunicipioNorm === municipioNorm;

            let nucleoMatch = true;
            if (!isNucleoTodos) {
                if (activeView === 'painel2') {
                    nucleoMatch = f.properties.nm_cerurb_normalizado === nucleoNorm;
                } else if (activeView === 'painel1') {
                    // Tenta match direto por NUCLEO_CASALEGAL_PAINEIS, depois por NUCLEO e NM_CERURB
                    const correspondingNucleo   = painelToNucleoNorm?.[nucleoNorm];
                    const correspondingNmCerurb = painelToNmCerurbNorm?.[nucleoNorm];
                    nucleoMatch =
                        f.properties.nucleo_casalegal_paineis_normalizado === nucleoNorm ||
                        (correspondingNucleo   && f.properties.nucleo_normalizado    === correspondingNucleo)   ||
                        (correspondingNmCerurb && f.properties.nm_cerurb_normalizado === correspondingNmCerurb);
                } else {
                    nucleoMatch = f.properties.nucleo_normalizado === nucleoNorm;
                }
            }
            return territorioMatch && municipioMatch && nucleoMatch;
        });


        const planilhaFiltrada = planilhaData.filter(item => {
            const itemMunicipioNorm = normalizeString(item.municipio);
            const itemTerritorioNorm = municipioToTerritorioMap[itemMunicipioNorm] || ""; // Busca no mapa

            const territorioMatch = isTerritorioTodos || itemTerritorioNorm === territorioNorm;
            const municipioMatch = isMunicipioTodos || itemMunicipioNorm === municipioNorm;
            const nucleoMatch = isNucleoTodos || normalizeString(item.nucleo) === nucleoNorm;
            return territorioMatch && municipioMatch && nucleoMatch;
        });
        

        const dadoscontroleFiltrados = planilhaControleData.filter(item => {
        // Tenta pegar o valor de 'municipio', 'MUNICIPIO', ou 'Município'
            const rawMunicipio = item.municipio || item.MUNICIPIO || item.Municipio || "";
            const rawNucleo = item.nucleo || item.NUCLEO || item.Nucleo || "";
            const rawTerritorio = item.territorio || item.TERRITORIO || "";

            const itemMunicipioNorm = normalizeString(rawMunicipio);
            
            // Tenta pegar território do mapa ou do item
            const itemTerritorioNorm = municipioToTerritorioMap[itemMunicipioNorm] || normalizeString(rawTerritorio);

            const territorioMatch = isTerritorioTodos || itemTerritorioNorm === territorioNorm;
            const municipioMatch = isMunicipioTodos || itemMunicipioNorm === municipioNorm;
            const nucleoMatch = isNucleoTodos || normalizeString(rawNucleo) === nucleoNorm;
            
            return territorioMatch && municipioMatch && nucleoMatch;
        });


        const nucleosExternosFiltrados = nucleosExternos.filter(n => {
            const itemMunicipioNorm = normalizeString(n.cidadeNucleo);
            const itemTerritorioNorm = municipioToTerritorioMap[itemMunicipioNorm] || "";

            const territorioMatch = isTerritorioTodos || itemTerritorioNorm === territorioNorm;
            const municipioMatch = isMunicipioTodos || itemMunicipioNorm === municipioNorm;
            // A 'key' de filtro para 'nucleosExternos' é sempre 'n.nome'
            const nucleoMatch = isNucleoTodos || normalizeForMatch(n.nome) === nucleoNorm;

            return territorioMatch && municipioMatch && nucleoMatch;
        });
        const planilhaFiltradaPorTerritorio = planilhaData.filter(item => {
            const itemMunicipioNorm = normalizeString(item.municipio);
            const itemTerritorioNorm = municipioToTerritorioMap[itemMunicipioNorm] || "";
            return isTerritorioTodos || itemTerritorioNorm === territorioNorm;
        });

        return { filteredFeatures, planilhaFiltrada, planilhaFiltradaPorTerritorio, nucleosExternosFiltrados, dadoscontroleFiltrados };
    }, [
        features,
        detailFeatures,
        planilhaData,
        planilhaControleData,
        nucleosExternos,
        selectedTerritorio,
        selectedMunicipio,
        selectedNucleo,
        municipioToTerritorioMap,
        activeView
    ]);


    // CALCULA VALORES DERIVADOS A PARTIR DOS DADOS JÁ FILTRADOS
    const { filteredFeatures, planilhaFiltrada, planilhaFiltradaPorTerritorio, nucleosExternosFiltrados, dadoscontroleFiltrados } = filteredData;


    const resumoPlanilha = useMemo(() => {

        const municipiosUnicos = new Set();

        const resultado = planilhaFiltrada.reduce((acc, item) => {
            acc.total_nucleos += 1;
            acc.total_unidades += item.quantidade_unidades_total || 0;
            acc.unidades_concluidas += item.unidades_concluidas || 0;
            acc.total_unidades_sentenca += item.quantidade_unidades_sentenca || 0;
            acc.total_registros_emitidos += item.total_registros_emitidos || 0;
            acc.enviados_cartorio += item.enviados_cartorio || 0;
            acc.emitidos_central += item.emitidos_central || 0;

            // Cálculo da diferença (Regularizados Externamente)
            const sentencas = item.quantidade_unidades_sentenca || 0;
            const concluidas = item.unidades_concluidas || 0;
            // Corrigido para sentencas - concluidas para refletir o que "sobra"
            const diff =  concluidas - sentencas;
            acc.total_regularizados_externamente += diff;

            // Adiciona o município ao Set (ignora duplicatas automaticamente)
            if (item.municipio) {
                municipiosUnicos.add(normalizeString(item.municipio));
            }

            return acc;
        }, { 
            total_nucleos: 0, 
            total_unidades: 0, 
            unidades_concluidas: 0,
            total_unidades_sentenca: 0, 
            total_registros_emitidos: 0, 
            enviados_cartorio: 0,
            emitidos_central: 0,
            total_regularizados_externamente: 0,
            total_municipios: 0 // Inicializador
        });

        // Atribuímos a contagem final do Set ao resultado
        resultado.total_municipios = municipiosUnicos.size;

        return resultado;
    }, [planilhaFiltrada]);

    const estatisticasFiltradas = useMemo(() => {
        const stats = {
            territorios: {},
            por_mes: {}
        };

        // Estatísticas de território: usa filtro só por território (ignora município/núcleo)
        // para exibir o território completo mesmo quando um município está selecionado
        planilhaFiltradaPorTerritorio.forEach(item => {
            if (item.territorio && item.territorio !== '') {
                const terr = item.territorio;
                if (!stats.territorios[terr]) {
                    stats.territorios[terr] = { count: 0, unidades: 0, unidades_sentenca: 0, registros_emitidos: 0, enviados_cartorio: 0, emitidos_central: 0 };
                }
                stats.territorios[terr].count++;
                stats.territorios[terr].unidades += item.quantidade_unidades_total || 0;
            }
        });

        // Estatísticas por mês: usa filtro completo (município incluso)
        planilhaFiltrada.forEach(item => {
            if (item.mes_sentenca && item.mes_sentenca !== '') {
                 const mes = item.mes_sentenca;
                 if (!stats.por_mes[mes]) {
                    stats.por_mes[mes] = { count: 0, unidades: 0, unidades_sentenca: 0 };
                 }
                 stats.por_mes[mes].count++;
                 stats.por_mes[mes].unidades += item.quantidade_unidades_total || 0;
                 stats.por_mes[mes].unidades_sentenca += item.quantidade_unidades_sentenca || 0;
            }
        });

        return stats;

    }, [planilhaFiltrada, planilhaFiltradaPorTerritorio]);

    // Lógica idêntica, apenas otimizada com um único loop 'forEach'
    const resumoSPI = useMemo(() => {
        const counts = { totalLotes: 0, totalQuadras: 0, totalNucleos: 0 };
        const municipiosComNucleos = new Set();

        filteredFeatures.forEach(f => {
            const tipo = f.properties.TIPO?.toLowerCase();
            if (tipo === "lote") counts.totalLotes++;
            else if (tipo === "quadra") counts.totalQuadras++;
            else if (tipo === "nucleo") {
                counts.totalNucleos++;
                if (f.properties.MUNICIPIO) {
                    municipiosComNucleos.add(f.properties.MUNICIPIO);
                }
            }
        });
        
        return { ...counts, municipiosComNucleos: municipiosComNucleos.size };
    }, [filteredFeatures]);



    // Lógica idêntica ao seu 'useEffect' que calculava statusCounts
    const statusCounts = useMemo(() => {
        return nucleosExternosFiltrados.reduce((acc, n) => {
            const status = n.quantidadeImovelStatus || {};
            for (const [key, value] of Object.entries(status)) {
                acc[key] = (acc[key] || 0) + value;
            }
            return acc;
        }, {});
    }, [nucleosExternosFiltrados]);

    // Mapa de status externo: "nm_cerurb_norm-quadra-lote" → status
    const imoveisStatusMap = useMemo(() => {
        const map = {};
        const normPart = (s) => {
            const str = String(s ?? '').trim();
            return /^\d+$/.test(str) ? String(parseInt(str, 10)) : str.toLowerCase();
        };
        for (const n of nucleosExternos) {
            if (!Array.isArray(n.imoveisResumo)) continue;
            for (const im of n.imoveisResumo) {
                if (!im.status) continue;
                const key = normalizeForMatch(im.nucleo) + '-' + normPart(im.quadra) + '-' + normPart(im.lote);
                map[key] = im.status;
            }
        }
        return map;
    }, [nucleosExternos]);

    // Features do mapa
    const lotes = useMemo(() => {
        const municipioNorm = normalizeString(selectedMunicipio);
        const isTeresina = municipioNorm === "teresina";
        const mostrarPorNucleo = selectedNucleo !== "Todos";
        const mostrarPorMunicipio = selectedMunicipio !== "Todos" && !isTeresina;
        if (!mostrarPorNucleo && !mostrarPorMunicipio) return [];

        const normPart = (s) => {
            const str = String(s ?? '').trim();
            return /^\d+$/.test(str) ? String(parseInt(str, 10)) : str.toLowerCase();
        };

        const enriched = filteredFeatures
            .filter(f => f.properties.TIPO?.toLowerCase() === "lote")
            .map(f => {
                const p = f.properties;
                const key = normalizeForMatch(p.NM_CERURB) + '-' + normPart(p.QUADRA) + '-' + normPart(p.LOTE);
                const statusExterno = imoveisStatusMap[key];
                if (!statusExterno) return f;
                return { ...f, properties: { ...p, SITUACAO: statusExterno } };
            });
        return selectedStatus
            ? enriched.filter(f => f.properties.SITUACAO === selectedStatus)
            : enriched;
    }, [filteredFeatures, selectedNucleo, selectedMunicipio, imoveisStatusMap, selectedStatus]);

    const quadras = useMemo(() => {
        if (selectedNucleo !== "Todos") return filteredFeatures.filter(f => f.properties.TIPO?.toLowerCase() === "quadra");
        return [];
    }, [filteredFeatures, selectedNucleo]);
    

    const nucleosFiltrados = useMemo(() => filteredFeatures.filter(f => f.properties.TIPO?.toLowerCase() === "nucleo"), [filteredFeatures]);
    
    // const municipiosMap = useMemo(() => {
    //     let municipios = features.filter(f => f.properties.TIPO?.toLowerCase() === "municipio");
    //     const territorioNorm = normalizeString(selectedTerritorio);
    //     const municipioNorm = normalizeString(selectedMunicipio);

    //     // Filtra por território se um território for selecionado
    //     if (selectedTerritorio !== "Todos") {
    //         municipios = municipios.filter(f => f.properties.territorio_normalizado === territorioNorm);
    //     }

    //     // Filtra por município se um município específico for selecionado
    //     if (selectedMunicipio !== "Todos") {
    //         municipios = municipios.filter(f => f.properties.municipio_normalizado === municipioNorm);
    //     }

    //     return municipios;
    // }, [features, selectedTerritorio, selectedMunicipio]);

    const municipiosMap = useMemo(() => {
        // IMPORTANTE: Não filtre aqui! Deixe o getStyle do mapa decidir a cor.
        return features.filter(f => f.properties.TIPO?.toLowerCase() === "municipio");
    }, [features]);

    // const territorios = useMemo(() => {
    //     const allTerritorios = features.filter(f => f.properties.TIPO?.toLowerCase() === "territorio");

    //     if (selectedTerritorio === "Todos") {
    //         return allTerritorios;
    //     }

    //     const territorioNorm = normalizeString(selectedTerritorio);
    //     return allTerritorios.filter(f => f.properties.territorio_normalizado === territorioNorm);
    // }, [features, selectedTerritorio]); // Adiciona dependência de filtro


    const territorios = useMemo(() => {
        // Retorna TODOS os territórios para o mapa
        return features.filter(f => f.properties.TIPO?.toLowerCase() === "territorio");
    }, [features]);

    // Bounds do mapa (LÓGICA PRESERVADA E OTIMIZADA)
    const filteredBounds = useMemo(() => {
        // [MODIFICADO] Lógica de busca do 'nucleoFeature' precisa ser condicional
        if (selectedNucleo !== "Todos") {
            const nucleoNorm = normalizeForMatch(selectedNucleo);
            const nucleoFeature = features.find(f => {
                const tipoMatch = f.properties.TIPO?.toLowerCase() === "nucleo";
                if (!tipoMatch) return false;

                // Lógica condicional para encontrar o núcleo certo
                if (activeView === 'painel2') return f.properties.nm_cerurb_normalizado === nucleoNorm;
                if (activeView === 'painel1') return f.properties.nucleo_casalegal_paineis_normalizado === nucleoNorm;
                return f.properties.nucleo_normalizado === nucleoNorm;
            });
            
            return nucleoFeature ? getBoundsFromGeometry(nucleoFeature.geometry) : null;
        }
        
        return filteredFeatures.reduce((acc, f) => {
            const bounds = getBoundsFromGeometry(f.geometry);
            if (!bounds) return acc;
            if (!acc) return bounds;
            
            acc[0][0] = Math.min(acc[0][0], bounds[0][0]);
            acc[0][1] = Math.min(acc[0][1], bounds[0][1]);
            acc[1][0] = Math.max(acc[1][0], bounds[1][0]);
            acc[1][1] = Math.max(acc[1][1], bounds[1][1]);
            return acc;
        }, null);

    }, [selectedNucleo, filteredFeatures, features, activeView]); 

    return {
        // Dados brutos e de estado
        loading,
        loadingDetails,
        error,
        features,
        planilhaData: planilhaFiltrada,
        dadoscontroleFiltrados, 
        // territorioStats, (agora parte de estatisticasPlanilha)
        
        // Listas para filtros
        territoriosOptions,
        municipiosFiltrados,
        municipiosComNucleos,
        municipioToTerritorio,
        nucleoToMunicipioMap,
        nucleoGidMap,
        nucleos,

        // Dados filtrados e derivados
        lotes,
        quadras,
        nucleosFiltrados,
        municipiosMap,
        territorios, 
        statusCounts,
        filteredBounds,
        nucleosExternosFiltrados,
        resumoSPI,
        
        // --- Props Atualizadas/Novas ---
        resumoPlanilha, 
        territorioStats, // (Mantido para compatibilidade)
        estatisticasPlanilha, 
        territorioStatsFiltrado: estatisticasFiltradas.territorios, 
        estatisticasPorMesFiltrado: estatisticasFiltradas.por_mes,
    };
}
